import { Button } from "@freightsimple/ui-core/Controls/Button";
import { ControlPresentationProvider } from "@freightsimple/ui-core/Controls/ControlPresentationProvider";
import { Dialog } from "@freightsimple/ui-core/Dialog/Dialog";
import { Stack } from "@freightsimple/ui-core/ComponentLayout/Stack";
import type { Broker } from "@freightsimple/generated-dashboard-openapi-client/models/Broker";
import type { SavedBroker } from "@freightsimple/generated-dashboard-openapi-client/models/SavedBroker";
import type { UUID } from "@/Shared/types";
import { useState } from "react";
import BrokersBox from "@/Shared/Brokers/CustomComponents/BrokersBox";
import BrokersSavedDropdown from "@/Shared/Brokers/Dropdowns/BrokersSavedDropdown";
import { useBrokersAddBrokerDialog } from "@/Features/Brokers/Exports/Hooks/useBrokersAddBrokerDialog";
import { useBrokersModifyBrokerDialog } from "@/Features/Brokers/Exports/Hooks/useBrokersModifyBrokerDialog";

interface BookShipmentChangeBrokerDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  savedBrokerId: UUID;
  savedBrokers: Array<SavedBroker>;
  onBrokerChanged: (updatedBroker: Broker) => void;
}

// "Select Customs Broker" for shipments whose company has multiple saved
// brokers: switch via the dropdown (applies immediately), modify the selected
// one, or add a new one. The add/modify wizards stack above this dialog.
export function BookShipmentChangeBrokerDialog(
  props: BookShipmentChangeBrokerDialogProps,
) {
  const [selectedSavedBrokerId, setSelectedSavedBrokerId] = useState(
    props.savedBrokerId,
  );
  const addBrokerDialog = useBrokersAddBrokerDialog();
  const modifyBrokerDialog = useBrokersModifyBrokerDialog();

  async function onAddBroker() {
    const newSavedBroker = await addBrokerDialog.show();
    if (newSavedBroker !== undefined) {
      props.onBrokerChanged(newSavedBroker.broker);
      props.onOpenChange(false);
    }
  }

  const selectedSavedBroker = props.savedBrokers.find(function (sb) {
    return sb.savedBrokerId === selectedSavedBrokerId;
  });

  function onSwitch(newSavedBrokerId: UUID) {
    setSelectedSavedBrokerId(newSavedBrokerId);
    const newSelectedBroker = props.savedBrokers.find(function (sb) {
      return sb.savedBrokerId === newSavedBrokerId;
    })?.broker;

    if (newSelectedBroker === undefined) {
      throw new Error("Missing saved broker");
    }
    props.onBrokerChanged(newSelectedBroker);
  }

  async function onModify() {
    if (selectedSavedBroker === undefined) {
      return;
    }
    const result = await modifyBrokerDialog.show(selectedSavedBroker);
    if (result !== undefined) {
      props.onBrokerChanged(result.broker);
    }
    props.onOpenChange(false);
  }

  return (
    <>
      <Dialog
        open={props.open}
        onOpenChange={props.onOpenChange}
        title="Select Customs Broker"
        description="Select from one of your saved customs broker for this shipment"
        footer={
          <Button primary onClick={() => props.onOpenChange(false)}>
            Done
          </Button>
        }
      >
        <ControlPresentationProvider presentation="underlined">
          <Stack gap={6} align="start">
            <BrokersSavedDropdown
              savedBrokerId={selectedSavedBrokerId}
              savedBrokers={props.savedBrokers}
              onSetSavedBrokerId={onSwitch}
            />
            {selectedSavedBroker !== undefined && (
              <BrokersBox
                key={selectedSavedBrokerId}
                broker={selectedSavedBroker.broker}
                onModify={onModify}
                showMenu={false}
                onDelete={undefined}
                preferredNorthbound={false}
                preferredSouthbound={false}
                onSelectPreferred={undefined}
              />
            )}
            <Button size="link" onClick={onAddBroker}>
              Add new customs broker
            </Button>
          </Stack>
        </ControlPresentationProvider>
      </Dialog>
      {addBrokerDialog.dialogs}
      {modifyBrokerDialog.dialogs}
    </>
  );
}
